/* eslint-disable store/split-to-modules */
import businessService from '@/modules/business/services/businessService';
import webAIWidgetApiService from '../services/webAIWidgetApiService';
import formatTimeSlotsByDayToHumanReadable from '../utils/businessHoursFormatter';
import { DEFAULT_CONFIG, FALLBACK_BUSINESS_NAME } from '../constants/webAIWidgetDefaults';

const URL_KEYS = ['logo_url'];
const OMIT_KEYS = ['id', 'uid', 'business_id', 'created_at', 'updated_at'];

/**
 * Keys merged from the request payload into the committed config after save (API may omit or strip nulls).
 */
const CONFIG_PATCH_KEYS = [
  'widget_title',
  'button_text',
  'brand_color',
  'text_color',
  'logo_url',
  'welcome_message',
  'welcome_buttons',
  'actions',
  'auto_popup_enabled',
  'auto_popup_delay',
  'include_contact_info',
  'include_services',
  'include_products',
  'include_booking_policies',
  'include_terms_of_service',
  'guidance_urls',
  'faq_and_guidance',
  'lead_capture_text',
  'lead_intent_data',
  'business_hours',
];

const LEAD_INTENT_MAX_LEN = 100;
const LEAD_DATA_ITEMS_MAX_LEN = 200;
const LEAD_MESSAGE_TO_VISITOR_MAX_LEN = 200;
const LEAD_INTENT_DATA_MAX_ITEMS = 10;
const LEAD_INTENT_GENERAL = 'General inquiry';
const LEAD_INTENT_PLACEHOLDER = 'Add intent here';

function normalizeLeadIntentData(value) {
  if (!Array.isArray(value)) return null;
  const list = value
    .slice(0, LEAD_INTENT_DATA_MAX_ITEMS)
    .map((item, index) => {
      const rawLeadIntent = String(item?.lead_intent ?? '').trim();
      const leadIntent = rawLeadIntent === LEAD_INTENT_PLACEHOLDER ? '' : rawLeadIntent;
      const leadDataItems = String(item?.lead_data_items ?? '').trim();
      const leadMessageToVisitor = String(item?.lead_message_to_visitor ?? '').trim();
      const isGeneral = index === 0 || leadIntent === LEAD_INTENT_GENERAL;
      let normalizedLeadIntent = null;
      if (isGeneral) normalizedLeadIntent = LEAD_INTENT_GENERAL;
      else if (leadIntent) normalizedLeadIntent = leadIntent.slice(0, LEAD_INTENT_MAX_LEN);
      return {
        lead_intent: normalizedLeadIntent,
        lead_data_items: leadDataItems ? leadDataItems.slice(0, LEAD_DATA_ITEMS_MAX_LEN) : null,
        lead_message_to_visitor: leadMessageToVisitor
          ? leadMessageToVisitor.slice(0, LEAD_MESSAGE_TO_VISITOR_MAX_LEN)
          : null,
      };
    })
    .filter((row) => row.lead_intent || row.lead_data_items || row.lead_message_to_visitor);
  return list.length > 0 ? list : null;
}

/**
 * Build request payload for PUT main: snake_case, omit id/uid/business_id/dates,
 * empty URL fields as null.
 * @param {object} config - Full config (id, business_id, widget_title, actions, ...)
 * @param {string} [widgetTitle] - Override for widget_title
 * @returns {object} Payload for upsertMainConfig
 */
function buildRequestPayload(config, widgetTitle) {
  if (!config) return {};
  const out = { ...config };
  OMIT_KEYS.forEach((k) => delete out[k]);
  if (widgetTitle !== undefined) {
    out.widget_title = widgetTitle;
  }
  URL_KEYS.forEach((k) => {
    if (out[k] === '' || out[k] === undefined) {
      out[k] = null;
    }
  });
  if (out.guidance_urls === '') {
    out.guidance_urls = null;
  }
  if (out.guidance_urls != null && !Array.isArray(out.guidance_urls)) {
    out.guidance_urls = [out.guidance_urls];
  }
  if (out.business_hours === '') {
    out.business_hours = null;
  }
  if (out.lead_capture_text === '') {
    out.lead_capture_text = null;
  }
  if (out.lead_intent_data != null) {
    out.lead_intent_data = normalizeLeadIntentData(out.lead_intent_data);
  }
  if (out.welcome_message === '') {
    out.welcome_message = null;
  }
  if (out.button_text === '') {
    out.button_text = null;
  }
  if (out.widget_title === '') {
    out.widget_title = null;
  }
  if (out.faq_and_guidance === '') {
    out.faq_and_guidance = null;
  }
  if (out.brand_color === '') {
    out.brand_color = null;
  }
  if (out.text_color === '') {
    out.text_color = null;
  }
  return out;
}

const state = {
  config: null,
  business: null,
  rawBusinessName: null,
  isLoading: false,
  error: null,
  /** Available client portal actions for this business (from API or placeholder). Shape: [{ key, defaultLabel }]. */
  availablePortalActions: [],
  historyRefreshTrigger: 0,
  historyGoToEditorTrigger: 0,
  editorSaveTrigger: 0,
  editorGoToHistoryTrigger: 0,
  editorShowEmbedTrigger: 0,
  editorOpenMoreMenuTrigger: 0,
  editorMoreSelectTrigger: { timestamp: 0, identifier: null },
  defaultBusinessHours: null,
  defaultBusinessHoursError: null,
};

const getters = {
  config: (s) => s.config,
  business: (s) => s.business,
  rawBusinessName: (s) => s.rawBusinessName,
  isLoading: (s) => s.isLoading,
  error: (s) => s.error,
  availablePortalActions: (s) => s.availablePortalActions,
  historyRefreshTrigger: (s) => s.historyRefreshTrigger,
  historyGoToEditorTrigger: (s) => s.historyGoToEditorTrigger,
  editorSaveTrigger: (s) => s.editorSaveTrigger,
  editorGoToHistoryTrigger: (s) => s.editorGoToHistoryTrigger,
  editorShowEmbedTrigger: (s) => s.editorShowEmbedTrigger,
  editorOpenMoreMenuTrigger: (s) => s.editorOpenMoreMenuTrigger,
  editorMoreSelectTrigger: (s) => s.editorMoreSelectTrigger,
  defaultBusinessHours: (s) => s.defaultBusinessHours,
  defaultBusinessHoursError: (s) => s.defaultBusinessHoursError,
};

const mutations = {
  setConfig(state, config) {
    state.config = config;
  },
  setBusiness(state, business) {
    state.business = business;
  },
  setRawBusinessName(state, name) {
    state.rawBusinessName = name;
  },
  setLoading(state, value) {
    state.isLoading = value;
  },
  setError(state, error) {
    state.error = error;
  },
  setAvailablePortalActions(state, portalActions) {
    state.availablePortalActions = Array.isArray(portalActions) ? portalActions : [];
  },
  historyRefreshTrigger(state) {
    state.historyRefreshTrigger = Date.now();
  },
  historyGoToEditorTrigger(state) {
    state.historyGoToEditorTrigger = Date.now();
  },
  editorSaveTrigger(state) {
    state.editorSaveTrigger = Date.now();
  },
  editorGoToHistoryTrigger(state) {
    state.editorGoToHistoryTrigger = Date.now();
  },
  editorShowEmbedTrigger(state) {
    state.editorShowEmbedTrigger = Date.now();
  },
  editorOpenMoreMenuTrigger(state) {
    state.editorOpenMoreMenuTrigger = Date.now();
  },
  setEditorMoreSelect(state, identifier) {
    state.editorMoreSelectTrigger = { timestamp: Date.now(), identifier };
  },
  setDefaultBusinessHours(state, value) {
    state.defaultBusinessHours = value;
    state.defaultBusinessHoursError = null;
  },
  setDefaultBusinessHoursError(state, message) {
    state.defaultBusinessHours = null;
    state.defaultBusinessHoursError = message;
  },
};

const actions = {
  async fetchAvailablePortalActions({ commit, rootGetters }) {
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) return;
    try {
      const list = await webAIWidgetApiService.getAvailablePortalActions(businessUid);
      commit('setAvailablePortalActions', list);
    } catch (_) {
      commit('setAvailablePortalActions', []);
    }
  },

  async fetchDefaultBusinessHours({ commit, rootGetters }) {
    try {
      const staffOwner = rootGetters['StaffStore/staffOwner'];
      const uid = staffOwner?.weekly_availability_uid
        && String(staffOwner.weekly_availability_uid).trim();
      if (!uid) {
        commit('setDefaultBusinessHours', null);
        return;
      }
      const data = await webAIWidgetApiService.getWeeklyAvailabilityGeneral(uid);
      const slots = data?.time_slots_by_day;
      if (!Array.isArray(slots) || slots.length === 0) {
        commit('setDefaultBusinessHours', null);
        return;
      }
      const formatted = formatTimeSlotsByDayToHumanReadable(slots);
      commit('setDefaultBusinessHours', formatted);
    } catch (err) {
      const status = err?.response?.status;
      const message = status === 403
        ? 'web_ai_widget.content_tab.business_hours_unable_to_retrieve'
        : err?.message || null;
      commit('setDefaultBusinessHoursError', message);
    }
  },

  async fetchDefaults({ commit, rootGetters, dispatch }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      if (!businessUid) {
        commit('setError', 'Business context required');
        return;
      }
      await dispatch('StaffStore/fetchAllStaff', null, { root: true });
      await dispatch('fetchDefaultBusinessHours');
      await webAIWidgetApiService.getAvailablePortalActions(businessUid).then((list) => {
        commit('setAvailablePortalActions', list);
      }).catch(() => commit('setAvailablePortalActions', []));
      const result = await webAIWidgetApiService.getMainConfig(businessUid);
      let rawName = null;
      try {
        const data = await businessService.fetchBusiness(businessUid);
        rawName = data?.business?.business?.name || null;
      } catch (_) {
        // Ignore; rawName stays null
      }
      if (result.config !== null) {
        const loadedConfig = { ...result.config };
        commit('setConfig', loadedConfig);
        commit('setBusiness', result.business);
        if (rawName) commit('setRawBusinessName', rawName);
        return;
      }
      const widgetTitleDefault = rawName || result.business?.name || FALLBACK_BUSINESS_NAME;
      commit('setRawBusinessName', widgetTitleDefault);
      const brandColor = rootGetters['BusinessStore/businessSettingsBrandColor'];
      if (brandColor == null || String(brandColor).trim() === '') {
        commit('setError', 'web_ai_widget.error_business_color_unavailable');
        return;
      }
      const config = {
        ...DEFAULT_CONFIG,
        business_id: businessUid,
      };
      // Persist via PUT .../main so the widget uid exists before the editor preview loads (embed/public config).
      await dispatch('saveConfig', config);
    } catch (err) {
      const status = err?.response?.status;
      const message = status === 403 ? 'web_ai_widget.error_forbidden' : err?.message || 'Failed to load';
      commit('setError', message);
    } finally {
      commit('setLoading', false);
    }
  },

  triggerHistoryRefresh({ commit }) {
    commit('historyRefreshTrigger');
  },
  triggerHistoryGoToEditor({ commit }) {
    commit('historyGoToEditorTrigger');
  },
  triggerEditorSave({ commit }) {
    commit('editorSaveTrigger');
  },
  triggerEditorGoToHistory({ commit }) {
    commit('editorGoToHistoryTrigger');
  },
  triggerEditorShowEmbed({ commit }) {
    commit('editorShowEmbedTrigger');
  },
  triggerEditorOpenMoreMenu({ commit }) {
    commit('editorOpenMoreMenuTrigger');
  },
  triggerEditorMoreSelect({ commit }, identifier) {
    commit('setEditorMoreSelect', identifier);
  },
  async saveConfig({ commit, state, rootGetters }, payload) {
    commit('setError', null);
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) {
      const err = new Error('Business context required');
      throw err;
    }
    const configSource = payload ?? state.config;
    const widgetTitle = configSource && Object.prototype.hasOwnProperty.call(configSource, 'widget_title')
      ? configSource.widget_title
      : state.business?.name;
    const requestPayload = buildRequestPayload(configSource, widgetTitle);
    const result = await webAIWidgetApiService.upsertMainConfig(
      businessUid,
      requestPayload,
    );
    const configToCommit = { ...result.config };
    CONFIG_PATCH_KEYS.forEach((key) => {
      if (requestPayload[key] !== undefined) {
        configToCommit[key] = requestPayload[key];
      }
    });
    commit('setConfig', configToCommit);
    commit('setBusiness', result.business);
    return configToCommit;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
